import { useEffect, useState, MouseEvent } from "react";
import {
  CloseOutlined,
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  FullscreenOutlined,
  LeftOutlined,
  RightOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { ExcelPreview } from "./ExcelPreview";
import { CommonDrawer } from "../../../base/components/drawer/CommonDrawer";
import { CommonTooltip } from "../../../base/components/tooltips/CommonTooltip";
import { CommonButton } from "../../../base/components/buttons/CommonButton";
import { CommonSpin } from "../../../base/components/spin/CommonSpin";

export interface PreviewFileLike {
  id: string;
  originalFileName: string;
  fileSize?: string | number;
  contentType?: string;
}

function getExt(name: string) {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

type FileKind = "image" | "pdf" | "text" | "code" | "csv" | "audio" | "excel" | "office" | "unknown";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "ico", "tiff", "tif"];
const TEXT_EXTS = ["txt", "md", "log", "json", "xml", "yaml", "yml", "toml", "ini", "env", "conf", "cfg", "properties"];
const CODE_EXTS = [
  "js",
  "jsx",
  "ts",
  "tsx",
  "html",
  "css",
  "scss",
  "less",
  "py",
  "java",
  "kt",
  "go",
  "rs",
  "c",
  "cpp",
  "h",
  "cs",
  "php",
  "rb",
  "sh",
  "bash",
  "sql",
  "graphql",
  "vue",
  "svelte",
];
const AUDIO_EXTS = ["mp3", "wav", "ogg", "flac", "aac", "m4a", "opus", "weba"];
const EXCEL_EXTS = ["xls", "xlsx", "ods"];
const OFFICE_EXTS = ["doc", "docx", "ppt", "pptx", "odt", "odp"];

function classify(name: string): FileKind {
  const ext = getExt(name);
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (ext === "csv") return "csv";
  if (TEXT_EXTS.includes(ext)) return "text";
  if (CODE_EXTS.includes(ext)) return "code";
  if (AUDIO_EXTS.includes(ext)) return "audio";
  if (EXCEL_EXTS.includes(ext)) return "excel";
  if (OFFICE_EXTS.includes(ext)) return "office";
  return "unknown";
}

export interface FilePreviewProps {
  file: PreviewFileLike | null;
  blobUrl?: string;
  loading?: boolean;
  onClose: () => void;
  onDownload?: (file: PreviewFileLike) => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

function ImagePreview({ url, name }: { url: string; name: string }) {
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [drag, setDrag] = useState({ active: false, startX: 0, startY: 0, tx: 0, ty: 0, dx: 0, dy: 0 });
  const [fit, setFit] = useState(true);

  const zoom = (delta: number) => {
    setFit(false);
    setScale((s) => Math.min(5, Math.max(0.1, s + delta)));
  };

  const onMouseDown = (e: MouseEvent) => {
    setDrag((d) => ({ ...d, active: true, startX: e.clientX - d.dx, startY: e.clientY - d.dy }));
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!drag.active) return;
    setDrag((d) => ({ ...d, dx: e.clientX - d.startX, dy: e.clientY - d.startY }));
  };
  const onMouseUp = () => setDrag((d) => ({ ...d, active: false }));

  return (
    <div className="relative h-full overflow-hidden bg-[var(--color-background-secondary)]">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 bg-[var(--color-background-primary)] border-[0.5px] border-[var(--color-border-secondary)] rounded-lg py-1 px-2">
        <CommonTooltip title="Thu nhỏ">
          <CommonButton icon={<ZoomOutOutlined />} onClick={() => zoom(-0.2)} />
        </CommonTooltip>
        <span className="leading-6 text-xs min-w-[44px] text-center text-[var(--color-text-secondary)]">{Math.round(scale * 100)}%</span>
        <CommonTooltip title="Phóng to">
          <CommonButton icon={<ZoomInOutlined />} onClick={() => zoom(0.2)} />
        </CommonTooltip>
        <CommonTooltip title="Vừa khung">
          <CommonButton
            icon={<FullscreenOutlined />}
            onClick={() => {
              setScale(1);
              setFit(true);
              setDrag((d) => ({ ...d, dx: 0, dy: 0 }));
            }}
          />
        </CommonTooltip>
        <div className="w-px bg-[var(--color-border-tertiary)] m-[2px]" />
        <CommonTooltip title="Xoay trái">
          <CommonButton icon={<RotateLeftOutlined />} onClick={() => setRotate((r) => r - 90)} />
        </CommonTooltip>
        <CommonTooltip title="Xoay phải">
          <CommonButton icon={<RotateRightOutlined />} onClick={() => setRotate((r) => r + 90)} />
        </CommonTooltip>
      </div>
      <div
        className={`w-full h-full flex items-center justify-center select-none ${drag.active ? "cursor-grabbing" : "cursor-grab"}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={(e) => {
          e.preventDefault();
          zoom(e.deltaY < 0 ? 0.15 : -0.15);
        }}
      >
        <img
          src={url}
          alt={name}
          draggable={false}
          style={{
            maxWidth: fit ? "100%" : undefined,
            maxHeight: fit ? "100%" : undefined,
            transform: `translate(${drag.dx}px, ${drag.dy}px) scale(${scale}) rotate(${rotate}deg)`,
            transition: drag.active ? "none" : "transform 0.15s ease",
            objectFit: "contain",
          }}
        />
      </div>
    </div>
  );
}

function PdfPreview({ url }: { url: string }) {
  return <iframe src={`${url}#toolbar=1&navpanes=0`} title="PDF Preview" className="w-full h-full border-0" />;
}

function TextPreview({ url, isCode }: { url: string; isCode: boolean }) {
  const [content, setContent] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then(setContent)
      .catch(() => setErr(true));
  }, [url]);

  if (err) return <Empty icon="⚠️" label="Không thể đọc file" />;
  if (content === null) return <CenterSpin />;

  return (
    <div className="h-full overflow-auto py-4 px-5">
      <pre className={`m-0 text-[13px] leading-[1.7] whitespace-pre-wrap break-all ${isCode ? "font-mono" : ""}`}>{content}</pre>
    </div>
  );
}

function CsvPreview({ url }: { url: string }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((txt) => {
        const parsed = txt
          .split("\n")
          .filter(Boolean)
          .map((line) => {
            const result: string[] = [];
            let cur = "";
            let inQ = false;
            for (let i = 0; i < line.length; i++) {
              const ch = line[i];
              if (ch === '"') {
                inQ = !inQ;
                continue;
              }
              if (ch === "," && !inQ) {
                result.push(cur.trim());
                cur = "";
                continue;
              }
              cur += ch;
            }
            result.push(cur.trim());
            return result;
          });
        setRows(parsed);
      })
      .catch(() => setErr(true));
  }, [url]);

  if (err) return <Empty icon="⚠️" label="Không thể đọc CSV" />;
  if (!rows) return <CenterSpin />;

  const [head, ...body] = rows;

  return (
    <div className="h-full overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className="py-2 px-3 text-left bg-[var(--color-background-secondary)] border-b border-[var(--color-border-secondary)] font-medium whitespace-nowrap text-[var(--color-text-primary)]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-[var(--color-background-primary)]" : "bg-[var(--color-background-secondary)]"}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="py-[6px] px-3 border-b-[0.5px] border-[var(--color-border-tertiary)] text-[var(--color-text-primary)] max-w-[240px] truncate"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AudioPreview({ url, name }: { url: string; name: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-5">
      <div className="text-[64px]">🎵</div>
      <div className="text-sm text-[var(--color-text-secondary)]">{name}</div>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio controls src={url} className="w-[min(400px,90%)]" />
    </div>
  );
}

type ZipEntry = {
  name: string;
  compression: number;
  compressedSize: number;
  localHeaderOffset: number;
};

type PptxSlide = {
  name: string;
  lines: string[];
};

const textDecoder = new TextDecoder("utf-8");

function isPublicHttpUrl(url: string) {
  return /^https?:\/\//i.test(url);
}

function findEndOfCentralDirectory(view: DataView) {
  const minOffset = Math.max(0, view.byteLength - 0xffff - 22);
  for (let offset = view.byteLength - 22; offset >= minOffset; offset--) {
    if (view.getUint32(offset, true) === 0x06054b50) return offset;
  }
  return -1;
}

function parseZipEntries(buffer: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buffer);
  const eocdOffset = findEndOfCentralDirectory(view);
  if (eocdOffset < 0) throw new Error("Invalid ZIP file");

  const totalEntries = view.getUint16(eocdOffset + 10, true);
  let offset = view.getUint32(eocdOffset + 16, true);
  const entries: ZipEntry[] = [];

  for (let i = 0; i < totalEntries; i++) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;

    const compression = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const nameBytes = new Uint8Array(buffer, offset + 46, nameLength);
    const name = textDecoder.decode(nameBytes);

    entries.push({ name, compression, compressedSize, localHeaderOffset });
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function inflateRaw(data: Uint8Array) {
  const DecompressionStreamCtor = (window as any).DecompressionStream;
  if (!DecompressionStreamCtor) throw new Error("DecompressionStream is not supported");

  const stream = new Blob([data as any]).stream().pipeThrough(new DecompressionStreamCtor("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readZipEntry(buffer: ArrayBuffer, entry: ZipEntry) {
  const view = new DataView(buffer);
  const localOffset = entry.localHeaderOffset;
  if (view.getUint32(localOffset, true) !== 0x04034b50) throw new Error("Invalid ZIP local header");

  const nameLength = view.getUint16(localOffset + 26, true);
  const extraLength = view.getUint16(localOffset + 28, true);
  const dataOffset = localOffset + 30 + nameLength + extraLength;
  const compressedData = new Uint8Array(buffer, dataOffset, entry.compressedSize);

  if (entry.compression === 0) return compressedData;
  if (entry.compression === 8) return inflateRaw(compressedData);

  throw new Error("Unsupported ZIP compression");
}

async function readOfficeXmlEntries(url: string, predicate: (name: string) => boolean) {
  const buffer = await fetch(url).then((res) => res.arrayBuffer());
  const entries = parseZipEntries(buffer).filter((entry) => predicate(entry.name));

  return Promise.all(
    entries.map(async (entry) => ({
      name: entry.name,
      xml: textDecoder.decode(await readZipEntry(buffer, entry)),
    })),
  );
}

function parseXml(xml: string) {
  return new DOMParser().parseFromString(xml, "application/xml");
}

function getElementsByLocalName(root: Document | Element, localName: string): Element[] {
  return Array.from(root.getElementsByTagName("*")).filter((element) => element.localName === localName);
}

function collectOfficeText(root: Element) {
  let text = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();

  while (node) {
    const element = node as Element;
    if (element.localName === "t") text += element.textContent ?? "";
    if (element.localName === "tab") text += "\t";
    if (element.localName === "br" || element.localName === "cr") text += "\n";
    node = walker.nextNode();
  }

  return text.replace(/[ \t]+\n/g, "\n").trim();
}

async function extractDocxParagraphs(url: string) {
  const [documentXml] = await readOfficeXmlEntries(url, (name) => name === "word/document.xml");
  if (!documentXml) return [];

  const document = parseXml(documentXml.xml);
  const paragraphs = getElementsByLocalName(document, "p").map(collectOfficeText).filter(Boolean);

  if (paragraphs.length) return paragraphs;
  return getElementsByLocalName(document, "t").map((element) => element.textContent?.trim() ?? "").filter(Boolean);
}

async function extractPptxSlides(url: string): Promise<PptxSlide[]> {
  const slides = await readOfficeXmlEntries(url, (name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name));

  return slides
    .sort((a, b) => Number(a.name.match(/slide(\d+)\.xml$/i)?.[1] ?? 0) - Number(b.name.match(/slide(\d+)\.xml$/i)?.[1] ?? 0))
    .map((slide) => {
      const document = parseXml(slide.xml);
      const lines = getElementsByLocalName(document, "p").map(collectOfficeText).filter(Boolean);
      const fallbackLines = lines.length ? lines : getElementsByLocalName(document, "t").map((element) => element.textContent?.trim() ?? "").filter(Boolean);
      return { name: slide.name, lines: fallbackLines };
    });
}

function DocxPreviewContent({ paragraphs }: { paragraphs: string[] }) {
  if (!paragraphs.length) return <Empty icon="!" label="KhÃ´ng tÃ¬m tháº¥y ná»™i dung vÄƒn báº£n trong file Word" />;

  return (
    <div className="h-full overflow-auto bg-slate-100 px-4 py-6">
      <div className="mx-auto min-h-full max-w-4xl rounded bg-white px-8 py-7 text-sm leading-7 text-slate-800 shadow-sm">
        {paragraphs.map((paragraph, index) => (
          <p key={`${index}-${paragraph.slice(0, 20)}`} className="mb-3 whitespace-pre-wrap">
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  );
}

function PptxPreviewContent({ slides }: { slides: PptxSlide[] }) {
  if (!slides.length) return <Empty icon="!" label="KhÃ´ng tÃ¬m tháº¥y slide trong file PowerPoint" />;

  return (
    <div className="h-full overflow-auto bg-slate-100 p-5">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {slides.map((slide, index) => (
          <div key={slide.name} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-2 text-xs font-medium text-slate-500">Slide {index + 1}</div>
            <div className="aspect-video overflow-auto px-6 py-5 text-sm leading-6 text-slate-800">
              {slide.lines.length ? (
                slide.lines.map((line, lineIndex) => (
                  <p key={`${lineIndex}-${line.slice(0, 20)}`} className="mb-2 whitespace-pre-wrap">
                    {line}
                  </p>
                ))
              ) : (
                <p className="text-slate-400">Slide khÃ´ng cÃ³ ná»™i dung vÄƒn báº£n</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OfficePreview({ url, name, onDownload }: { url: string; name: string; onDownload?: () => void }) {
  const ext = getExt(name);
  const [docxParagraphs, setDocxParagraphs] = useState<string[] | null>(null);
  const [pptxSlides, setPptxSlides] = useState<PptxSlide[] | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setErr(false);
    setDocxParagraphs(null);
    setPptxSlides(null);

    if (ext !== "docx" && ext !== "pptx") return;

    const load = async () => {
      try {
        if (ext === "docx") {
          const paragraphs = await extractDocxParagraphs(url);
          if (!cancelled) setDocxParagraphs(paragraphs);
          return;
        }

        const slides = await extractPptxSlides(url);
        if (!cancelled) setPptxSlides(slides);
      } catch {
        if (!cancelled) setErr(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [ext, url]);

  if (ext === "docx") {
    if (err) return <UnknownPreview name={name} onDownload={onDownload} />;
    if (!docxParagraphs) return <CenterSpin />;
    return <DocxPreviewContent paragraphs={docxParagraphs} />;
  }

  if (ext === "pptx") {
    if (err) return <UnknownPreview name={name} onDownload={onDownload} />;
    if (!pptxSlides) return <CenterSpin />;
    return <PptxPreviewContent slides={pptxSlides} />;
  }

  if (isPublicHttpUrl(url)) {
    const encoded = encodeURIComponent(url);
    return <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`} title="Office Preview" className="w-full h-full border-0" />;
  }

  return <UnknownPreview name={name} onDownload={onDownload} />;
}

function UnknownPreview({ name, size, onDownload }: { name: string; size?: number; onDownload?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--color-text-secondary)]">
      <span className="text-[56px]">📄</span>
      <div className="text-[15px] font-medium text-[var(--color-text-primary)]">{name}</div>
      {size && <div className="text-xs">{size}</div>}
      <div className="text-[13px]">Định dạng này không hỗ trợ xem trực tiếp</div>
      {onDownload && (
        <CommonButton icon={<DownloadOutlined />} onClick={onDownload}>
          Tải về để xem
        </CommonButton>
      )}
    </div>
  );
}

function CenterSpin() {
  return (
    <div className="flex items-center justify-center h-full">
      <CommonSpin size="large" />
    </div>
  );
}

function Empty({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 text-[var(--color-text-secondary)]">
      <span className="text-[40px]">{icon}</span>
      <span className="text-sm">{label}</span>
    </div>
  );
}

export const FilePreview = ({ file, blobUrl, loading, onClose, onDownload, onPrev, onNext, hasPrev, hasNext }: FilePreviewProps) => {
  const kind = file ? classify(file.originalFileName) : "unknown";

  const print = () => {
    if (!blobUrl) return;
    const w = window.open(blobUrl);
    w?.print();
  };

  const renderContent = () => {
    if (!file) return null;
    if (loading) return <CenterSpin />;
    if (!blobUrl) return <Empty icon="📭" label="Không có dữ liệu" />;

    switch (kind) {
      case "image":
        return <ImagePreview url={blobUrl} name={file.originalFileName} />;
      case "pdf":
        return <PdfPreview url={blobUrl} />;
      case "csv":
        return <CsvPreview url={blobUrl} />;
      case "text":
        return <TextPreview url={blobUrl} isCode={false} />;
      case "code":
        return <TextPreview url={blobUrl} isCode />;
      case "audio":
        return <AudioPreview url={blobUrl} name={file.originalFileName} />;
      case "excel":
        return <ExcelPreview url={blobUrl} />;
      case "office":
        return <OfficePreview url={blobUrl} name={file.originalFileName} onDownload={() => onDownload?.(file)} />;
      default:
        return <UnknownPreview name={file.originalFileName} size={file.fileSize as number} onDownload={() => file && onDownload?.(file)} />;
    }
  };

  return (
    <CommonDrawer
      open={!!file}
      onClose={onClose}
      hideHeader
      destroyOnClose
      bodyPadding={0}
      bodyOverflow="hidden"
      rootClassName="[&_.ant-drawer-mask]:backdrop-blur-[4px]"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex items-center justify-between py-[10px] px-[14px] border-b-[0.5px] border-[var(--color-border-tertiary)] bg-[var(--color-background-primary)] shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <CommonButton icon={<CloseOutlined />} onClick={onClose} />
            <FileKindBadge kind={kind} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--color-text-primary)] overflow-hidden text-ellipsis whitespace-nowrap max-w-[50vw]">
                {file?.originalFileName}
              </div>
              {file?.fileSize && <div className="text-[11px] text-[var(--color-text-secondary)]">{file.fileSize as number}</div>}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {(kind === "image" || kind === "pdf") && (
              <CommonTooltip title="In">
                <CommonButton icon={<PrinterOutlined />} onClick={print} />
              </CommonTooltip>
            )}
            {onDownload && file && (
              <CommonTooltip title="Tải xuống">
                <CommonButton icon={<DownloadOutlined />} onClick={() => onDownload(file)} />
              </CommonTooltip>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden relative">
          {renderContent()}

          {hasPrev && (
            <button
              onClick={onPrev}
              className="absolute left-[10px] top-1/2 -translate-y-1/2 z-20 bg-[var(--color-background-primary)] border-[0.5px] border-[var(--color-border-secondary)] rounded-lg w-9 h-9 cursor-pointer flex items-center justify-center text-[var(--color-text-primary)]"
            >
              <LeftOutlined />
            </button>
          )}
          {hasNext && (
            <button
              onClick={onNext}
              className="absolute right-[10px] top-1/2 -translate-y-1/2 z-20 bg-[var(--color-background-primary)] border-[0.5px] border-[var(--color-border-secondary)] rounded-lg w-9 h-9 cursor-pointer flex items-center justify-center text-[var(--color-text-primary)]"
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>
    </CommonDrawer>
  );
};

const KIND_META: Record<FileKind, { label: string; color: string; bg: string }> = {
  image: { label: "Ảnh", color: "#0e7490", bg: "#cffafe" },
  pdf: { label: "PDF", color: "#991b1b", bg: "#fee2e2" },
  text: { label: "Text", color: "#374151", bg: "#f3f4f6" },
  code: { label: "Code", color: "#1d4ed8", bg: "#dbeafe" },
  csv: { label: "CSV", color: "#166534", bg: "#dcfce7" },
  audio: { label: "Audio", color: "#6b21a8", bg: "#f3e8ff" },
  excel: { label: "Excel", color: "#166534", bg: "#dcfce7" },
  office: { label: "Office", color: "#9a3412", bg: "#ffedd5" },
  unknown: { label: "File", color: "#374151", bg: "#f3f4f6" },
};

function FileKindBadge({ kind }: { kind: FileKind }) {
  const m = KIND_META[kind];
  return (
    <span className="text-[11px] font-medium py-[2px] px-[7px] rounded-[4px] shrink-0" style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}
